/**
 * Global toast registry — a single ref<Toast[]> shared across the
 * whole app. Any component imports `useToast()` to push a new toast;
 * the `<ToastShelf />` rendered at the App root consumes the list.
 *
 * Severity levels:
 *   - error   (red, persists 6s)    — for AdminError / network failures
 *   - success (green, persists 3s)  — for "saved" / "request submitted"
 *   - warning (amber, persists 5s)  — for partial successes
 *   - info    (neutral, persists 4s)— for status hints
 *
 * The auto-dismiss timer fires on `setTimeout`; the component itself
 * also exposes a manual dismiss handler so the user can click an x.
 */
import { ref } from "vue";

export type ToastSeverity = "error" | "success" | "warning" | "info";

export interface Toast {
  id: number;
  severity: ToastSeverity;
  message: string;
  /** Optional extra detail shown below the headline (e.g. an error stack). */
  detail?: string;
}

const _toasts = ref<Toast[]>([]);
let _nextId = 1;

const TTL_BY_SEVERITY: Record<ToastSeverity, number> = {
  error:   6000,
  warning: 5000,
  info:    4000,
  success: 3000,
};

function push(severity: ToastSeverity, message: string, detail?: string): number {
  const id = _nextId++;
  _toasts.value = [..._toasts.value, { id, severity, message, detail }];
  // Stable timer: each toast gets its own timeout regardless of others.
  window.setTimeout(() => dismiss(id), TTL_BY_SEVERITY[severity]);
  return id;
}

function dismiss(id: number): void {
  _toasts.value = _toasts.value.filter((t) => t.id !== id);
}

/**
 * Compose-time accessor. Components call this to push toasts or
 * (rarely) to read the list directly. The list ref is shared, so
 * every caller sees the same toasts.
 */
export function useToast() {
  return {
    toasts: _toasts,
    error:   (message: string, detail?: string) => push("error", message, detail),
    success: (message: string, detail?: string) => push("success", message, detail),
    warning: (message: string, detail?: string) => push("warning", message, detail),
    info:    (message: string, detail?: string) => push("info", message, detail),
    dismiss,
  };
}
